import type { ImpactVisualProfile } from './impactVisualProfile'
import type { ImpactCueKind } from './store'
import type { PerceptualMorphogenesisFrame } from '@inyron/animation'

export type VfxQualityTier='low'|'balanced'|'high'|'ultra'
export type VfxPriority='normal'|'major'|'critical'
export type VfxRuntimeCaps={
  hardwareConcurrency?:number
  deviceMemoryGb?:number
  reducedMotion?:boolean
  runtimePressure?:'normal'|'high'|'critical'
}
export type VfxRuntimePolicy={
  tier:VfxQualityTier
  priority:VfxPriority
  fragmentScale:number
  lightScale:number
  motionScale:number
}

const TIER_FACTORS:Record<VfxQualityTier,[number,number,number]>={
  low:[.45,.58,.72],
  balanced:[.7,.78,.88],
  high:[1,1,1],
  ultra:[1.15,1.08,1.05],
}

export function resolveVfxQualityTier(caps:VfxRuntimeCaps={}):VfxQualityTier{
  const cores=caps.hardwareConcurrency,mem=caps.deviceMemoryGb
  if(cores==null&&mem==null)return'high'
  if((cores??0)>=12&&(mem??8)>=8)return'ultra'
  if((cores??0)>=8&&(mem??6)>=6)return'high'
  if((cores??0)>=4&&(mem??4)>=4)return'balanced'
  return'low'
}export function impactVfxPriority(kind:ImpactCueKind):VfxPriority{
  if(kind==='parry'||kind==='guard-break')return'critical'
  if(kind==='throw')return'major'
  return'normal'
}

export function browserVfxCaps():VfxRuntimeCaps{
  if(typeof navigator==='undefined')return{}
  const nav=navigator as Navigator&{deviceMemory?:number}
  const reduced=typeof matchMedia==='function'&&matchMedia('(prefers-reduced-motion: reduce)').matches
  return{hardwareConcurrency:nav.hardwareConcurrency,deviceMemoryGb:nav.deviceMemory,reducedMotion:reduced}
}

export function resolveVfxRuntimePolicy(kind:ImpactCueKind,caps:VfxRuntimeCaps={}):VfxRuntimePolicy{
  const tier=resolveVfxQualityTier(caps),priority=impactVfxPriority(kind)
  let[fragmentScale,lightScale,motionScale]=TIER_FACTORS[tier]
  if(priority==='critical'){
    fragmentScale=Math.max(fragmentScale,.78)
    lightScale=Math.max(lightScale,.82)
  }else if(priority==='major'){
    fragmentScale=Math.max(fragmentScale,.62)
    lightScale=Math.max(lightScale,.7)
  }
  if(caps.runtimePressure==='high'){
    fragmentScale*=.78;lightScale*=.84;motionScale*=.9
  }else if(caps.runtimePressure==='critical'){
    fragmentScale*=.58;lightScale*=.7;motionScale*=.72
  }
  if(priority==='critical'){
    fragmentScale=Math.max(fragmentScale,.62)
    lightScale=Math.max(lightScale,.72)
  }
  if(caps.reducedMotion){
    fragmentScale*=.8
    lightScale*=.78
    motionScale*=.35
  }
  return{tier,priority,fragmentScale,lightScale,motionScale}
}const clamp=(value:number,min:number,max:number)=>Math.max(min,Math.min(max,value))

export function applyVfxRuntimePolicy(
  profile:ImpactVisualProfile,
  kind:ImpactCueKind,
  caps:VfxRuntimeCaps={},
  perceptual?:PerceptualMorphogenesisFrame,
):ImpactVisualProfile{
  const policy=resolveVfxRuntimePolicy(kind,caps)
  const densityScale=perceptual?.vfxDensity==null?1:.95+perceptual.vfxDensity*.2
  const persistenceScale=perceptual?.particlePersistence==null?1:.94+perceptual.particlePersistence*.16
  const lightScale=perceptual?.lightPulse==null?1:clamp(1+perceptual.lightPulse*.18,.82,1.18)
  const critical=policy.priority==='critical'
  const fragmentCount=clamp(Math.round(profile.fragmentCount*policy.fragmentScale*densityScale),2,12)
  const breakRays=profile.breakRays===0?0:clamp(Math.round(profile.breakRays*policy.fragmentScale*densityScale),1,8)
  return{
    ...profile,
    fragmentCount,
    breakRays,
    lightIntensity:profile.lightIntensity*policy.lightScale*lightScale,
    lightDistance:profile.lightDistance*Math.max(.72,policy.lightScale)*Math.max(.9,lightScale),
    fragmentSpin:profile.fragmentSpin*policy.motionScale*persistenceScale,
    spin:profile.spin*policy.motionScale*persistenceScale,
    rotationZ:profile.rotationZ*policy.motionScale,
    growth:profile.growth*policy.motionScale*persistenceScale,
    directionalLength:profile.directionalLength*policy.motionScale*(.96+densityScale*.04),
    ringOpacity:critical?Math.max(profile.ringOpacity,.82):profile.ringOpacity,
    coreOpacity:critical?Math.max(profile.coreOpacity,.58):profile.coreOpacity,
  }
}
